// SPDX-License-Identifier: BUSL-1.1

/**
 * Config editor page — edit JSON configs with version timeline.
 *
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api-client';
import { ConfigEditorSkeleton } from '@/components/skeletons';
import { type ConfigSurface, type ConfigVersion, STATUS_COLORS } from '@/types/api';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { showSuccess, showApiError } from '@/lib/toast';

interface ConfigDetailResponse {
  surface: ConfigSurface;
  versions: ConfigVersion[];
}

export function ConfigEditorPage() {
  const { key } = useParams<{ key: string }>();
  const qc = useQueryClient();
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [selectedEnv, setSelectedEnv] = useState<string>('production');
  const [editorContent, setEditorContent] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['configs', key],
    queryFn: () => apiFetch<ConfigDetailResponse>(`/configs/${key}`),
    enabled: !!key,
  });

  const versions = useMemo(() => data?.versions ?? [], [data?.versions]);
  const active = versions.find((v) => v.version === selectedVersion);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['configs', key] });

  const environments = useMemo(() => data?.surface?.environments ?? ['production'], [data?.surface?.environments]);

  useEffect(() => {
    if (versions.length > 0 && selectedVersion === null) setSelectedVersion(versions[0].version);
  }, [versions, selectedVersion]);

  useEffect(() => {
    if (environments.length > 0 && selectedEnv === 'production' && !environments.includes('production')) {
      setSelectedEnv(environments[0]);
    }
  }, [environments, selectedEnv]);

  useEffect(() => {
    if (active) { setEditorContent(JSON.stringify(active.config, null, 2)); setParseError(null); }
  }, [active]);

  const handleChange = useCallback((value: string) => {
    setEditorContent(value);
    try { JSON.parse(value); setParseError(null); } catch (e) { setParseError((e as Error).message); }
  }, []);

  const saveDraft = useMutation({
    mutationFn: () => apiFetch(`/configs/${key}/versions`, {
      method: 'POST', body: JSON.stringify({ config: JSON.parse(editorContent), environment: selectedEnv }),
    }),
    onSuccess: () => { showSuccess('Draft saved'); invalidate(); },
    onError: showApiError,
  });
  const stage = useMutation({
    mutationFn: () => apiFetch(`/configs/${key}/versions/${selectedVersion}/stage`, {
      method: 'POST',
    }),
    onSuccess: () => { showSuccess(`v${selectedVersion} staged`); invalidate(); },
    onError: showApiError,
  });
  const promote = useMutation({
    mutationFn: () => apiFetch(`/configs/${key}/versions/${selectedVersion}/promote`, {
      method: 'POST',
    }),
    onSuccess: () => { showSuccess(`v${selectedVersion} promoted`); invalidate(); },
    onError: showApiError,
  });
  const rollback = useMutation({
    mutationFn: () => apiFetch(`/configs/${key}/versions/${selectedVersion}/rollback`, {
      method: 'POST',
    }),
    onSuccess: () => { showSuccess(`v${selectedVersion} rolled back`); invalidate(); },
    onError: showApiError,
  });

  if (isLoading) return <ConfigEditorSkeleton />;
  if (!data) return <p className="text-neutral-500">Config not found.</p>;

  const canStage = active?.status === 'draft';
  const canPromote = active?.status === 'staged';
  const canRollback = active?.status === 'promoted';

  return (
    <div className="flex h-full flex-col gap-4">
      <div>
        <h1 className="font-mono text-2xl font-bold text-neutral-100">{data.surface.key}</h1>
        <p className="text-sm text-neutral-400">{data.surface.name}{data.surface.description && ` — ${data.surface.description}`}</p>
      </div>

      <div className="flex min-h-0 flex-1 gap-4">
        {/* Version timeline */}
        <aside className="w-48 shrink-0 overflow-y-auto rounded border border-neutral-800 bg-neutral-900 p-3">
          <h2 className="mb-3 text-xs font-medium uppercase tracking-wider text-neutral-500">Versions</h2>
          {versions.length >= 2 && (
            <Link to={`/configs/${key}/diff?from=${versions[1].version}&to=${versions[0].version}`}
              className="mb-3 block rounded border border-neutral-700 px-3 py-1.5 text-center text-xs text-neutral-400 hover:bg-neutral-800 hover:text-neutral-200">
              Compare Versions
            </Link>
          )}
          <ul className="space-y-2">
            {versions.map((v) => (
              <li key={v.version}>
                <Button
                  variant="ghost"
                  onClick={() => setSelectedVersion(v.version)}
                  className={`h-auto w-full flex-col items-start justify-start px-3 py-2 text-sm ${
                    v.version === selectedVersion
                      ? 'bg-neutral-800 text-neutral-100 hover:bg-neutral-800'
                      : 'text-neutral-400 hover:bg-neutral-800/50'
                  }`}
                >
                  <span>
                    <span className="font-mono font-medium">v{v.version}</span>
                    <span className={`ml-2 rounded px-1.5 py-0.5 text-xs ${STATUS_COLORS[v.status]}`}>{v.status}</span>
                  </span>
                  <span className="mt-1 text-xs text-neutral-500">{new Date(v.createdAt).toLocaleDateString()}</span>
                </Button>
              </li>
            ))}
          </ul>
        </aside>

        {/* JSON editor */}
        <div className="flex min-h-0 flex-1 flex-col rounded border border-neutral-800">
          <Textarea
            value={editorContent}
            onChange={(e) => handleChange(e.target.value)}
            spellCheck={false}
            className="min-h-0 flex-1 resize-none rounded-none border-0 bg-neutral-900 p-4 font-mono text-sm text-neutral-200 focus-visible:ring-0"
          />
          {parseError && (
            <div className="border-t border-red-900/50 bg-red-950/30 px-4 py-2 text-xs text-red-400">
              JSON error: {parseError}
            </div>
          )}
        </div>
      </div>

      {/* Action bar */}
      <div className="flex items-center gap-3 rounded border border-neutral-800 bg-neutral-900 px-4 py-3">
        <Select value={selectedEnv} onValueChange={(v) => v && setSelectedEnv(v)}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Environment" />
          </SelectTrigger>
          <SelectContent>
            {environments.map((env) => (
              <SelectItem key={env} value={env}>{env}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="secondary" disabled={!!parseError || saveDraft.isPending} onClick={() => saveDraft.mutate()}>
          {saveDraft.isPending ? 'Saving...' : 'Save Draft'}
        </Button>
        <Button variant="outline" className="border-yellow-800/60 text-yellow-200"
          disabled={!canStage || stage.isPending} onClick={() => stage.mutate()}>
          {stage.isPending ? 'Staging...' : 'Stage'}
        </Button>

        <Dialog open={promoteDialogOpen} onOpenChange={setPromoteDialogOpen}>
          <DialogTrigger
            render={<Button variant="outline" className="border-green-800/60 text-green-200"
              disabled={!canPromote || promote.isPending} />}
          >
            {promote.isPending ? 'Promoting...' : 'Promote'}
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Promotion</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-neutral-400">
              Are you sure you want to promote v{selectedVersion} to production?
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPromoteDialogOpen(false)}>Cancel</Button>
              <Button variant="default" onClick={() => { promote.mutate(); setPromoteDialogOpen(false); }}>
                Promote
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {canRollback && (
          <Button variant="destructive" disabled={rollback.isPending} onClick={() => rollback.mutate()}>
            {rollback.isPending ? 'Rolling back...' : 'Rollback'}
          </Button>
        )}

        {active && <span className="ml-auto text-xs text-neutral-500">v{active.version} &middot; {active.status}</span>}
      </div>
    </div>
  );
}
